import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { ArrowRightIcon, ImageIcon } from '../../components/icons/Icons';
import api from '../../services/api';
import AdminLayout from './AdminLayout';
import './PostEditor.css';

const PostEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = !!id;

  const [formData, setFormData] = useState({
    title: '',
    content: '',
    excerpt: '',
    featured_image: '',
    status: 'draft'
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const fetchPost = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/blog/admin/${id}`);
      setFormData({
        title: response.data.title || '',
        content: response.data.content || '',
        excerpt: response.data.excerpt || '',
        featured_image: response.data.featured_image || '',
        status: response.data.status || 'draft'
      });
    } catch (err) {
      setError('Failed to load post');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (isEditing) {
      fetchPost();
    }
  }, [isEditing, fetchPost]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleContentChange = (content) => {
    setFormData(prev => ({ ...prev, content }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const response = await api.post('/settings/upload', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFormData(prev => ({ ...prev, featured_image: response.data.url }));
    } catch (err) {
      alert('Failed to upload image');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      if (isEditing) {
        await api.put(`/blog/${id}`, formData);
      } else {
        await api.post('/blog', formData);
      }
      navigate('/admin/posts');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save post');
    } finally {
      setSaving(false);
    }
  };

  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['blockquote', 'link'],
      ['clean']
    ]
  };

  if (loading) {
    return (
      <AdminLayout>
        <div style={{ padding: '40px', textAlign: 'center' }}>Loading...</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <h1 className="admin-page-title">{isEditing ? 'Edit Post' : 'New Post'}</h1>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <form onSubmit={handleSubmit} className="post-editor-form">
        <div className="post-editor-main">
          <div className="admin-card">
            <div className="form-group">
              <label htmlFor="title" className="form-label">Title *</label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                className="form-input"
                required
                placeholder="Enter post title"
              />
            </div>

            
            <div className="form-group">
              <label className="form-label">Content *</label>
              <ReactQuill
                theme="snow"
                value={formData.content}
                onChange={handleContentChange}
                modules={modules}
                placeholder="Write your post content..."
              />
            </div>

            <div className="form-group">
              <label htmlFor="excerpt" className="form-label">Excerpt</label>
              <textarea
                id="excerpt"
                name="excerpt"
                value={formData.excerpt}
                onChange={handleChange}
                className="form-input"
                rows={3}
                placeholder="Brief summary of the post"
                maxLength={500}
              />
            </div>
          </div>
        </div>

        <div className="post-editor-sidebar">
          <div className="admin-card">
            <h3 className="admin-card-title">Publish</h3>
            <div className="form-group">
              <label htmlFor="status" className="form-label">Status</label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="form-input"
              >
                <option value="draft">Draft</option>
                <option value="published">Published</option>
              </select>
            </div>
            <button type="submit" className="btn btn-primary btn-full" disabled={saving}>
              {saving ? 'Saving...' : (isEditing ? 'Update Post' : 'Create Post')}
              <ArrowRightIcon size={16} />
            </button>
          </div>

          <div className="admin-card">
            <h3 className="admin-card-title">Featured Image</h3>
            {formData.featured_image && (
              <div className="featured-image-preview">
                <img src={formData.featured_image} alt="Featured" />
              </div>
            )}
            <label className="image-upload-btn">
              <ImageIcon size={18} />
              <span>{formData.featured_image ? 'Change Image' : 'Upload Image'}</span>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                style={{ display: 'none' }}
              />
            </label>
            {formData.featured_image && (
              <button
                type="button"
                className="btn-remove-image"
                onClick={() => setFormData(prev => ({ ...prev, featured_image: '' }))}
              >
                Remove Image
              </button>
            )}
          </div>
        </div>
      </form>
    </AdminLayout>
  );
};

export default PostEditor;
